/**
 * Workspace domain selector for mobile/desktop apps.
 *
 * Shown on first launch when no workspace domain is stored.
 * Validates the domain by calling /api/v1/auth/tenant/resolve,
 * then stores it for all subsequent API calls.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Globe, CheckCircle2, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Separator } from '../../components/ui/separator';
import {
  DEFAULT_DOMAIN,
  setStoredWorkspaceDomain,
  validateWorkspaceDomain,
  type WorkspaceTenantInfo as TenantInfo,
} from '../utils/workspaceDomain';

interface WorkspaceSelectorProps {
  onWorkspaceSelected: () => void;
}

export function WorkspaceSelector({ onWorkspaceSelected }: WorkspaceSelectorProps) {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const [mode, setMode] = useState<'subdomain' | 'custom'>('subdomain');
  const [customDomain, setCustomDomain] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvedTenant, setResolvedTenant] = useState<TenantInfo | null>(null);
  const [resolvedDomain, setResolvedDomain] = useState<string | null>(null);

  const toggleMode = () => {
    setMode((m) => (m === 'subdomain' ? 'custom' : 'subdomain'));
    setCustomDomain('');
    setError(null);
    setResolvedTenant(null);
  };

  const handleDefaultDomain = () => {
    // Use default domain — no custom domain stored means relative URLs (same as default)
    setStoredWorkspaceDomain(DEFAULT_DOMAIN);
    onWorkspaceSelected();
  };

  const handleCustomDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    const input = customDomain.trim().toLowerCase();

    if (!input) {
      setError(t('workspace.errorEmpty'));
      return;
    }

    let domain: string;
    if (mode === 'subdomain') {
      if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(input)) {
        setError(t('workspace.errorInvalidSubdomain'));
        return;
      }
      domain = `${input}.panluma.ai`;
    } else {
      if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/.test(input)) {
        setError(t('workspace.errorInvalidFormat'));
        return;
      }
      domain = input;
    }

    setIsValidating(true);
    setError(null);
    setResolvedTenant(null);

    try {
      const info = await validateWorkspaceDomain(domain);
      if (info) {
        setResolvedTenant(info);
        setResolvedDomain(domain);
      } else {
        setError(t('workspace.errorNotFound'));
      }
    } catch {
      setError(t('workspace.errorConnection'));
    } finally {
      setIsValidating(false);
    }
  };

  const handleConfirmCustomDomain = () => {
    if (resolvedDomain) {
      setStoredWorkspaceDomain(resolvedDomain);
      onWorkspaceSelected();
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <Card className="max-w-[400px] w-full">
        <CardContent className="p-6 sm:p-8">
          <div className="flex justify-center mb-2">
            <img src="/panluma-logo.png" alt={tCommon('app.name')} className="h-20 sm:h-24" />
          </div>
          <p className="text-sm text-muted-foreground text-center mb-6">
            {t('workspace.title')}
          </p>

          {/* Default domain option */}
          <Button
            className="w-full mb-4"
            variant="default"
            size="lg"
            onClick={handleDefaultDomain}
            data-testid="default-workspace-button"
          >
            <Globe className="h-4 w-4" />
            <span className="ms-2">{DEFAULT_DOMAIN}</span>
          </Button>

          <div className="relative my-4">
            <Separator />
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-xs text-muted-foreground">
              {t('workspace.or')}
            </span>
          </div>

          {/* Custom domain form */}
          <form onSubmit={handleCustomDomain}>
            <Label htmlFor="custom-domain" className="text-sm font-medium">
              {t('workspace.customDomainLabel')}
            </Label>
            <div className="mt-1.5 flex flex-col sm:flex-row gap-2">
              {mode === 'subdomain' ? (
                <div
                  dir="ltr"
                  className="flex h-11 w-full sm:flex-1 items-center rounded-md border border-input bg-transparent shadow-sm ring-offset-background focus-within:outline-none focus-within:ring-1 focus-within:ring-ring"
                >
                  <Input
                    id="custom-domain"
                    type="text"
                    /* eslint-disable-next-line i18next/no-literal-string */
                    placeholder="acme"
                    value={customDomain}
                    onChange={(e) => {
                      setCustomDomain(e.target.value);
                      setError(null);
                      setResolvedTenant(null);
                    }}
                    disabled={isValidating}
                    data-testid="custom-domain-input"
                    className="h-full border-0 bg-transparent text-end shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  <span className="pe-3 text-sm text-muted-foreground select-none whitespace-nowrap">.panluma.ai</span>
                </div>
              ) : (
                <Input
                  id="custom-domain"
                  type="text"
                  dir="ltr"
                  /* eslint-disable-next-line i18next/no-literal-string */
                  placeholder="app.example.com"
                  value={customDomain}
                  onChange={(e) => {
                    setCustomDomain(e.target.value);
                    setError(null);
                    setResolvedTenant(null);
                  }}
                  disabled={isValidating}
                  data-testid="custom-domain-input"
                  className="h-11 w-full sm:flex-1"
                />
              )}
              <Button
                type="submit"
                variant="outline"
                size="lg"
                className="w-full sm:w-auto"
                disabled={isValidating || !customDomain.trim()}
                data-testid="validate-domain-button"
              >
                {isValidating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t('workspace.connect')
                )}
              </Button>
            </div>
            <button
              type="button"
              onClick={toggleMode}
              className="mt-2 text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              data-testid="toggle-domain-mode-button"
            >
              {mode === 'subdomain' ? t('workspace.useCustomDomain') : t('workspace.useSubdomain')}
            </button>
          </form>

          {/* Error */}
          {error && (
            <Alert variant="destructive" className="mt-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Resolved tenant info */}
          {resolvedTenant && (
            <div className="mt-3 space-y-3">
              <Alert className="border-green-500/30 bg-green-500/5">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-foreground">
                  {resolvedTenant.tenant_name}
                </AlertDescription>
              </Alert>
              <Button
                className="w-full"
                onClick={handleConfirmCustomDomain}
                data-testid="confirm-workspace-button"
              >
                {t('workspace.continueToWorkspace')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
